import { useEffect, useRef, useState } from "react";
import { Globe, Loader2, Plus, Trash2, RefreshCw, Check, Copy, ImagePlus, X } from "lucide-react";
import {
  listDomains,
  addDomain,
  verifyDomain,
  deleteDomain,
  updateDomainBranding,
  type CustomDomain,
} from "../../features/vault/vaultApi";

/**
 * Read an image file and downscale it to a 64×64 PNG data URL — small enough to
 * store inline as the white-label favicon.
 */
function readFavicon(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 64;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas unsupported"));
      ctx.drawImage(img, 0, 0, 64, 64);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image"));
    };
    img.src = url;
  });
}

/** Title + favicon inputs reused by the add form and the per-domain editor. */
function BrandingFields({
  title,
  favicon,
  onTitle,
  onFavicon,
  titlePlaceholder,
}: {
  title: string;
  favicon: string | undefined;
  onTitle: (v: string) => void;
  onFavicon: (v: string | undefined) => void;
  titlePlaceholder: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setErr(null);
    try {
      onFavicon(await readFavicon(file));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Bad image");
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg border border-dashed border-ink-200 bg-canvas/40 text-ink-400 hover:border-gold-300"
          title="Upload favicon"
        >
          {favicon ? <img src={favicon} alt="favicon" className="h-full w-full object-cover" /> : <ImagePlus size={16} />}
        </button>
        {favicon && (
          <button type="button" onClick={() => onFavicon(undefined)} className="text-[10px] text-ink-400 hover:text-rose">
            remove
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/x-icon,image/jpeg,image/svg+xml"
          className="hidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </div>
      <div className="flex-1">
        <input
          value={title}
          onChange={(e) => onTitle(e.target.value)}
          placeholder={titlePlaceholder}
          maxLength={120}
          className="w-full rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
        />
        <p className="mt-1 text-[11px] text-ink-400">
          Browser-tab title + favicon shown when this domain is opened. Leave blank to keep the default.
        </p>
        {err && <p className="mt-1 text-[11px] text-rose">{err}</p>}
      </div>
    </div>
  );
}

/** Self-serve white-label domains: add → get a CNAME → verify → use on embeds. */
export default function DomainsSettings() {
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [input, setInput] = useState("");
  const [brandTitle, setBrandTitle] = useState("");
  const [brandFavicon, setBrandFavicon] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    listDomains().then((d) => setDomains(d.domains)).finally(() => setLoading(false));

  useEffect(() => {
    void refresh();
  }, []);

  const add = async () => {
    if (!input.trim()) return;
    setAdding(true);
    setError(null);
    try {
      await addDomain(input.trim(), {
        brandTitle: brandTitle.trim() || undefined,
        brandFavicon,
      });
      setInput("");
      setBrandTitle("");
      setBrandFavicon(undefined);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add domain");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (d: CustomDomain) => {
    if (!confirm(`Disconnect ${d.domain}?`)) return;
    await deleteDomain(d._id);
    await refresh();
  };

  return (
    <div className="surface-card mt-6 p-6">
      <div className="flex items-center gap-2">
        <Globe size={16} className="text-gold-500" />
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-400">Custom domains</h2>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-500">
        Share your configurators on your own domain. Add a subdomain like{" "}
        <span className="font-mono text-ink-700">viewer.yourbrand.com</span>, point the DNS record we give you,
        and your embed links become white-labeled — your title + favicon in the browser tab, with just a small
        "by Jewelitt Vault" credit. SSL is automatic.
      </p>

      <div className="mt-4 space-y-3 rounded-xl border border-ink-100 bg-canvas/30 p-4">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="viewer.yourbrand.com"
            className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-[14px] text-ink-900 focus:border-gold-400 focus:outline-none"
          />
          <button
            onClick={add}
            disabled={adding}
            className="btn-press flex items-center gap-1.5 rounded-lg bg-ink-900 px-4 py-2 text-[13px] font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
          >
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add domain
          </button>
        </div>
        <BrandingFields
          title={brandTitle}
          favicon={brandFavicon}
          onTitle={setBrandTitle}
          onFavicon={setBrandFavicon}
          titlePlaceholder="Browser tab title — e.g. Raetorian Fine Jewelry"
        />
      </div>
      {error && <p className="mt-2 text-[12.5px] text-rose">{error}</p>}

      {loading ? (
        <div className="mt-5 flex h-20 items-center justify-center text-ink-400">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : domains.length === 0 ? (
        <p className="mt-5 text-[12.5px] text-ink-400">No custom domains yet.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {domains.map((d) => (
            <DomainCard key={d._id} d={d} onChange={(u) => setDomains((ds) => ds.map((x) => (x._id === u._id ? u : x)))} onRemove={() => remove(d)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DomainCard({ d, onChange, onRemove }: { d: CustomDomain; onChange: (d: CustomDomain) => void; onRemove: () => void }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(d.brandTitle ?? "");
  const [favicon, setFavicon] = useState<string | undefined>(d.brandFavicon);
  const [saving, setSaving] = useState(false);

  const verify = async () => {
    setBusy(true);
    try {
      onChange(await verifyDomain(d._id));
    } finally {
      setBusy(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      onChange(await updateDomainBranding(d._id, { brandTitle: title.trim(), brandFavicon: favicon ?? null }));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-ink-100 bg-canvas/40 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {d.brandFavicon && <img src={d.brandFavicon} alt="" className="h-5 w-5 rounded border border-black/10" />}
          <span className="text-[14px] font-semibold text-ink-900">{d.domain}</span>
          <StatusPill status={d.status} />
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={verify}
            disabled={busy}
            className="flex items-center gap-1 rounded-lg border border-ink-200 px-2.5 py-1.5 text-[12px] font-medium text-ink-600 hover:border-gold-300 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Verify
          </button>
          <button onClick={onRemove} className="rounded-lg p-1.5 text-ink-400 hover:text-rose">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Branding summary / editor */}
      {editing ? (
        <div className="mt-3 rounded-lg bg-ink-50 p-3">
          <BrandingFields
            title={title}
            favicon={favicon}
            onTitle={setTitle}
            onFavicon={setFavicon}
            titlePlaceholder="Browser tab title"
          />
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={saveBranding}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-ink-900 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save branding
            </button>
            <button onClick={() => setEditing(false)} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-[12px] text-ink-500 hover:text-ink-800">
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-[12px] text-ink-500">
            {d.brandTitle ? (
              <>
                Tab title <span className="font-medium text-ink-700">“{d.brandTitle}”</span>
              </>
            ) : (
              <span className="text-ink-400">No custom tab title set</span>
            )}
          </p>
          <button onClick={() => setEditing(true)} className="text-[12px] font-medium text-gold-600 hover:text-gold-700">
            Edit branding
          </button>
        </div>
      )}

      {d.status === "pending" && (
        <div className="mt-3 rounded-lg bg-ink-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Add this DNS record at your registrar</p>
          <div className="mt-2 grid grid-cols-[60px_1fr_auto] items-center gap-x-3 gap-y-1 font-mono text-[12px]">
            <span className="text-ink-400">Type</span>
            <span className="text-ink-800">{d.dns.type}</span>
            <span />
            <span className="text-ink-400">Name</span>
            <span className="text-ink-800">{d.dns.name}</span>
            <CopyBtn value={d.dns.name} />
            <span className="text-ink-400">Value</span>
            <span className="truncate text-ink-800">{d.dns.value}</span>
            <CopyBtn value={d.dns.value} />
          </div>
          <p className="mt-2 text-[11px] text-ink-400">{d.dns.note} After adding it, click Verify (DNS can take a few minutes).</p>

          {/* dns.txt is added by the backend; type it locally to avoid a shared-type edit. */}
          {(() => {
            const txt = (d.dns as { txt?: { name: string; value: string } }).txt;
            return txt ? (
            <div className="mt-3 border-t border-ink-100 pt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                Or prove ownership with a TXT record
              </p>
              <div className="mt-2 grid grid-cols-[60px_1fr_auto] items-center gap-x-3 gap-y-1 font-mono text-[12px]">
                <span className="text-ink-400">Type</span>
                <span className="text-ink-800">TXT</span>
                <span />
                <span className="text-ink-400">Name</span>
                <span className="truncate text-ink-800">{txt.name}</span>
                <CopyBtn value={txt.name} />
                <span className="text-ink-400">Value</span>
                <span className="truncate text-ink-800">{txt.value}</span>
                <CopyBtn value={txt.value} />
              </div>
              <p className="mt-2 text-[11px] text-ink-400">Verifies ownership on any domain (including apex), even before the record above propagates.</p>
            </div>
            ) : null;
          })()}
        </div>
      )}
      {d.status === "active" && (
        <p className="mt-2 text-[12px] text-emerald">
          ✓ Live — embed links can use <span className="font-mono">https://{d.domain}/embed/…</span>
        </p>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: "pending" | "active" }) {
  return status === "active" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2 py-0.5 text-[10.5px] font-medium text-emerald">
      <Check size={10} /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gold-50 px-2 py-0.5 text-[10.5px] font-medium text-gold-700">
      Pending DNS
    </span>
  );
}

function CopyBtn({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
      className="rounded p-1 text-ink-400 hover:text-ink-800"
    >
      {done ? <Check size={12} className="text-emerald" /> : <Copy size={12} />}
    </button>
  );
}
